import { Button, ModalFooter } from "@heroui/react";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { updateOpportunityCardStatus } from "../../../services/api";
import { MarketplaceCardResponseDto } from "../../../types/types";
import ConfirmModal from "../../common/confirmModal";

interface RoleBasedActionsProps {
  viewCard: MarketplaceCardResponseDto;
  activeTab: "all" | "myCards";
  onClose: () => void;
  onRefresh?: () => void;
}

export const RoleBasedActions: React.FC<RoleBasedActionsProps> = ({
  viewCard,
  activeTab,
  onClose,
  onRefresh,
}) => {
  const [confirmPublishPopup, setConfirmPublishPopup] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const { t } = useTranslation("opportunities");

  const eventDateStr =
    viewCard.opportunityCardResponse?.formData?.details?.eventDate;
  const isExpired = (() => {
    if (!eventDateStr) return false;

    const eventDate = new Date(eventDateStr);
    const today = new Date();

    eventDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return eventDate < today;
  })();

  const handlePublish = useCallback(
    async (cardId: string) => {
      if (isPublishing) return;

      setIsPublishing(true);
      try {
        await updateOpportunityCardStatus(cardId);
        toast.success(t("card_published_successfully"));
        onRefresh?.();
        onClose();
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      } finally {
        setIsPublishing(false);
      }
    },
    [isPublishing, t, onClose, onRefresh],
  );

  return (
    <ModalFooter className="py-1">
      <div className="grid grid-cols-4 gap-1 w-full">
        <div className="flex gap-2">
          {activeTab === "myCards" && (
            <Button
              color={
                isExpired
                  ? "danger"
                  : viewCard.opportunityCardResponse?.status?.toUpperCase() ===
                      "PUBLISH"
                    ? "default"
                    : "success"
              }
              size="sm"
              disabled={
                viewCard.opportunityCardResponse?.status?.toUpperCase() ===
                  "PUBLISH" ||
                isExpired ||
                isPublishing
              }
              isLoading={isPublishing}
              onClick={() => {
                if (
                  viewCard.opportunityCardResponse?.status?.toUpperCase() !==
                    "PUBLISH" &&
                  !isExpired &&
                  viewCard.id
                ) {
                  setConfirmPublishPopup(true);
                }
              }}
            >
              {isExpired
                ? t("expired")
                : viewCard.opportunityCardResponse?.status?.toUpperCase() ===
                    "PUBLISH"
                  ? t("published")
                  : isPublishing
                    ? t("publishing")
                    : t("publish")}
            </Button>
          )}
        </div>
        <div></div>
        <div></div>
        <div className="flex items-center justify-end w-full gap-2 mb-4">
          <Button variant="flat" onClick={onClose} size="sm">
            {t("close")}
          </Button>
        </div>
        <ConfirmModal
          isOpen={confirmPublishPopup}
          onClose={() => setConfirmPublishPopup(false)}
          cardTitle={t("confirm_publish")}
          cardDescription={t("confirm_publish_description")}
          buttonText={t("confirm")}
          onConfirm={() => {
            if (viewCard.id) {
              handlePublish(viewCard.id);
            }
          }}
        />
      </div>
    </ModalFooter>
  );
};
